/* global Office, window */
// PDF export window ("PDF ექსპორტი" ribbon button): an Office dialog on the same taskpane.html,
// route #/pdf-export. An Office dialog has no Excel API, so the TASKPANE does the work:
// it reads the sheet list for the window, and runs the export when the window asks for it.
//
// dialog → taskpane: { type: "ready" } | { type: "export", sheets, options } | { type: "close" }
// taskpane → dialog: { type: "init", ...data } | { type: "init-error", message }
//                    | { type: "progress", text } | { type: "result", ok, summary | message }
import { canOpenDialog } from "../structure/dialogBridge";

export const PDF_ROUTE = "/pdf-export";
const DIALOG_SIZE_PCT = { width: 72, height: 86 };

export function isPdfRoute() {
  return (window.location.hash || "").replace(/^#/, "").startsWith(PDF_ROUTE);
}

const pdfUrl = () => {
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}#${PDF_ROUTE}?dialog=1`;
};

/**
 * Taskpane side. buildInit() → data for the window; runExport(sheets, onProgress, options) → summary.
 * Resolves with { close() } or null when dialogs are not available (the caller shows the in-pane page).
 */
export function openPdfDialog({ buildInit, runExport, onClosed }) {
  if (!canOpenDialog()) return Promise.resolve(null);
  return new Promise((resolve) => {
    Office.context.ui.displayDialogAsync(
      pdfUrl(),
      { ...DIALOG_SIZE_PCT, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          resolve(null);
          return;
        }
        const dialog = result.value;
        const send = (message) => {
          try {
            dialog.messageChild(JSON.stringify(message));
          } catch (e) {
            /* window already closed */
          }
        };
        let busy = false;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg) => {
          let msg;
          try {
            msg = JSON.parse(arg.message);
          } catch (e) {
            return;
          }
          if (msg.type === "ready") {
            try {
              send({ type: "init", ...(await buildInit()) });
            } catch (err) {
              send({ type: "init-error", message: err?.message || "ფურცლების ჩატვირთვა ვერ მოხერხდა" });
            }
          } else if (msg.type === "export") {
            if (busy) return;
            busy = true;
            try {
              const summary = await runExport(msg.sheets || [], (text) => send({ type: "progress", text }), msg.options || {});
              send({ type: "result", ok: true, summary });
            } catch (err) {
              send({ type: "result", ok: false, message: err?.message || "უცნობი შეცდომა" });
            } finally {
              busy = false;
            }
          } else if (msg.type === "close") {
            dialog.close();
            onClosed?.();
          }
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => onClosed?.());
        resolve({
          close: () => {
            try {
              dialog.close();
            } catch (e) {
              /* ignore */
            }
          },
        });
      }
    );
  });
}

/** Dialog side: listen to the taskpane's messages, then announce readiness. */
export function connectToTaskpane(onMessage) {
  return new Promise((resolve, reject) => {
    if (typeof Office === "undefined" || !Office.context?.ui?.addHandlerAsync) {
      reject(new Error("Not running in an Office dialog"));
      return;
    }
    Office.context.ui.addHandlerAsync(
      Office.EventType.DialogParentMessageReceived,
      (arg) => {
        try {
          onMessage(JSON.parse(arg.message));
        } catch (e) {
          /* ignore malformed messages */
        }
      },
      (registered) => {
        if (registered.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error("Dialog messaging is not available"));
          return;
        }
        sendToTaskpane({ type: "ready" });
        resolve();
      }
    );
  });
}

export function sendToTaskpane(message) {
  if (typeof Office !== "undefined" && Office.context?.ui?.messageParent) {
    Office.context.ui.messageParent(JSON.stringify(message));
  }
}
